import { useCallback } from 'react'
import { ContentItemViewNodesType } from '../../../../types/contentItemViewNode.type'
import { useShallowSelector } from '../../../../Workspace2Store'
import { useContentNodeUpdater } from './useContentNodeUpdater'
import { useTabReset } from './useTabReset'
import { useTrackChangesReset } from './useTrackChangesReset'

/**
 * Props for the `reRenderTree` function.
 *
 * @interface TreeMutationsProps
 * @property {Partial<ContentItemViewNodesType>[]} [updatedContentItemViewNodes] - The updated content item view nodes.
 * @property {number} [tabIndex] - The index of the tab to reset.
 * @property {boolean} [shouldReset] - Whether to reset the tree state.
 * @property {boolean} [shouldResetTrackChanges] - Whether to reset track changes data.
 * @property {boolean} [shouldRenderNonDefaultFilterTrackChanges] - Whether to render non-default filter track changes.
 */
interface TreeMutationsProps {
    updatedContentItemViewNodes?: Partial<ContentItemViewNodesType>[]
    tabIndex?: number
    shouldReset?: boolean
    shouldResetTrackChanges?: boolean
    shouldResetSuggestionsData?: boolean
    shouldSetIsSuggestionsDataUpdated?: boolean
    trackChangesUpdateFunction?: () => Promise<void>
}

/**
 * Throttle function to limit the rate at which a function can be called.
 *
 * @param {T} func - The function to throttle.
 * @param {number} limit - The time in milliseconds to wait before allowing the function to be called again.
 * @returns {(...args: Parameters<T>) => ReturnType<T> | undefined} - A throttled version of the function.
 */
export function throttle<T extends (...args: any[]) => any>(
    func: T,
    limit: number
): (...args: Parameters<T>) => ReturnType<T> | undefined {
    let inThrottle: boolean = false
    let lastResult: ReturnType<T>

    return function (
        this: any,
        ...args: Parameters<T>
    ): ReturnType<T> | undefined {
        if (!inThrottle) {
            lastResult = func.apply(this, args)
            inThrottle = true
            setTimeout(() => {
                inThrottle = false
            }, limit)
        }
        return lastResult
    }
}

/**
 * A custom hook that provides tree mutation utilities, including resetting filters, track changes, tabs, and performing targeted updates.
 *
 * @returns {{
 *   reRenderTree: (props: TreeMutationsProps) => void,
 *   rollbackChanges: () => void,
 *   updateTreeViewNodes: (updatedNodes: Partial<ContentItemViewNodesType>[]) => void
 * }} - An object containing tree mutation functions.
 */
export const useTreeMutations = () => {
    const resetTrackChanges = useTrackChangesReset()
    const resetTab = useTabReset()
    const { contentItemViewNodes } = useShallowSelector((s) => ({
        contentItemViewNodes: s.contentItemViewNodes,
    }))
    const updateTreeState = useContentNodeUpdater()

    /**
     * Re-renders the tree by resetting filters, track changes, tabs, or performing targeted updates.
     *
     * @param {TreeMutationsProps} props - The properties for re-rendering the tree.
     */
    const throttledRenderTree = useCallback(
        throttle((props: TreeMutationsProps) => {
            const {
                updatedContentItemViewNodes,
                tabIndex,
                shouldReset,
                shouldResetTrackChanges,
                shouldResetSuggestionsData,
                shouldSetIsSuggestionsDataUpdated,
                trackChangesUpdateFunction,
            } = props

            // Reset track changes
            resetTrackChanges(
                shouldResetTrackChanges,
                shouldResetSuggestionsData
            )

            // Reset tab or perform targeted updates
            if (tabIndex !== undefined) {
                resetTab(tabIndex)
            } else {
                updateTreeState(
                    updatedContentItemViewNodes,
                    shouldReset,
                    shouldSetIsSuggestionsDataUpdated
                )
            }

            // Update track changes incrementally if function is provided
            if (
                trackChangesUpdateFunction &&
                updatedContentItemViewNodes?.length
            ) {
                trackChangesUpdateFunction().catch((error) => {
                    console.error('Error updating track changes:', error)
                })
            }
        }, 0), // Throttle to once every 100ms
        [resetTrackChanges, resetTab, updateTreeState]
    )

    const reRenderTree = throttledRenderTree

    /**
     * Rolls back changes by restoring suggestion data and tree state.
     */
    const rollbackChanges = useCallback(() => {
        updateTreeState(contentItemViewNodes)
    }, [updateTreeState, contentItemViewNodes])

    return {
        reRenderTree,
        rollbackChanges,
    }
}
